import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    api.get("/auth/email-status")
      .then((r) => setEnabled(!!r.data?.enabled))
      .catch(() => setEnabled(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10"
         style={{ background: "linear-gradient(135deg,#7E22CE 0%, #4C1D95 100%)" }}>
      <Seo
        title="Recuperar contraseña · E.F. Concepción Madrid — Gestión Deportiva"
        description="Restablece la contraseña de tu cuenta de gestión deportiva del club E.F. Concepción Madrid. Introduce tu email y te enviaremos un enlace seguro para crear una nueva contraseña."
        path="/forgot-password"
      />
      <h1 className="sr-only">Recuperar contraseña del panel de gestión</h1>
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center space-y-2">
          <img src="/logo.jpg" alt="E.F. Concepción Madrid"
               className="w-16 h-16 mx-auto rounded-xl object-contain bg-white border border-slate-200" />
          <CardTitle className="text-xl font-black" style={{ fontFamily: "Nunito" }}>
            Recuperar contraseña
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!enabled ? (
            <div className="text-center space-y-3">
              <p className="text-sm text-slate-600">
                La recuperación de contraseña por email no está disponible en este momento.
                Pide ayuda al director deportivo del club.
              </p>
              <Link to="/login" className="text-sm text-purple-700 font-bold inline-flex items-center gap-1">
                <ArrowLeft className="h-4 w-4" /> Volver al inicio de sesión
              </Link>
            </div>
          ) : done ? (
            <div className="text-center space-y-3" data-testid="forgot-success">
              <MailCheck className="h-10 w-10 mx-auto text-emerald-500" />
              <p className="text-sm text-slate-700">
                Si la dirección está registrada en la plataforma, recibirás un email
                con un enlace para restablecer tu contraseña. <strong>Revisa también la
                carpeta de spam.</strong>
              </p>
              <p className="text-xs text-slate-500">
                El enlace caduca en 30 minutos.
              </p>
              <Link to="/login" className="text-sm text-purple-700 font-bold inline-flex items-center gap-1">
                <ArrowLeft className="h-4 w-4" /> Volver al inicio de sesión
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-sm text-slate-600">
                Introduce el email asociado a tu cuenta del club <strong>E.F. Concepción Madrid</strong>.
                Te enviaremos un enlace seguro para crear una contraseña nueva.
              </p>
              <p className="text-xs text-slate-500">
                El proceso es rápido y no requiere datos personales adicionales.
                Recuerda revisar la carpeta de <em>spam</em> si el correo tarda en llegar.
                Si tu cuenta no está asociada a un email o el enlace ha caducado,
                pide ayuda al director deportivo del club.
              </p>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  data-testid="forgot-email"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold"
                data-testid="forgot-submit"
              >
                {loading ? "Enviando..." : "Enviar enlace"}
              </Button>
              <Link to="/login" className="text-xs text-slate-500 hover:text-purple-700 inline-flex items-center gap-1">
                <ArrowLeft className="h-3 w-3" /> Volver al inicio de sesión
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
